import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { useAuthStore, useCartStore } from '../lib/store';
import { Check, Loader2, XCircle, ArrowRight } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const CheckoutSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const { getAuthHeader } = useAuthStore();
  const { fetchCart } = useCartStore();
  
  const [status, setStatus] = useState('loading');
  const [pollAttempts, setPollAttempts] = useState(0);
  const maxAttempts = 5;

  useEffect(() => {
    const pollPaymentStatus = async () => {
      if (!sessionId || pollAttempts >= maxAttempts) {
        if (pollAttempts >= maxAttempts) {
          setStatus('timeout');
        }
        return;
      }

      try {
        const res = await axios.get(
          `${API}/checkout/status/${sessionId}`,
          { headers: getAuthHeader() }
        );

        if (res.data.payment_status === 'paid') {
          setStatus('success');
          await fetchCart(); // Refresh cart (should be empty now)
        } else if (res.data.status === 'expired') {
          setStatus('expired');
        } else {
          // Keep polling
          setPollAttempts(prev => prev + 1);
          setTimeout(pollPaymentStatus, 2000);
        }
      } catch (error) {
        console.error('Error checking payment status:', error);
        setStatus('error');
      }
    };

    pollPaymentStatus();
  }, [sessionId, pollAttempts, getAuthHeader, fetchCart]);

  return (
    <div className="paper-texture min-h-screen flex items-center justify-center py-12">
      <div className="max-w-md w-full mx-auto px-4">
        <div className="bg-white rounded-xl border p-8 text-center">
          {status === 'loading' && (
            <>
              <Loader2 className="w-16 h-16 text-primary animate-spin mx-auto mb-6" />
              <h1 className="font-heading text-2xl font-bold text-secondary">Processing Payment</h1>
              <p className="text-muted-foreground mt-2">Please wait while we confirm your payment...</p>
            </>
          )}

          {status === 'success' && (
            <>
              <div className="w-20 h-20 bg-accent/10 rounded-full flex items-center justify-center mx-auto mb-6">
                <Check className="w-10 h-10 text-accent" />
              </div>
              <h1 className="font-heading text-3xl font-bold text-secondary" data-testid="success-title">Order Confirmed!</h1>
              <p className="text-muted-foreground mt-4">
                Thank you for your order. We'll send you an email confirmation shortly with your order details.
              </p>
              <p className="font-accent text-xl text-primary mt-4">
                Your fire cider is on its way!
              </p>
              <div className="flex flex-col gap-3 mt-8">
                <Link to="/account">
                  <Button className="w-full" data-testid="view-orders-btn">
                    View My Orders
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
                <Link to="/products">
                  <Button variant="outline" className="w-full" data-testid="continue-shopping-btn">
                    Continue Shopping
                  </Button>
                </Link>
              </div>
            </>
          )}

          {(status === 'error' || status === 'timeout' || status === 'expired') && (
            <>
              <div className="w-20 h-20 bg-destructive/10 rounded-full flex items-center justify-center mx-auto mb-6">
                <XCircle className="w-10 h-10 text-destructive" />
              </div>
              <h1 className="font-heading text-3xl font-bold text-secondary">
                {status === 'expired' ? 'Payment Expired' : 'Payment Issue'}
              </h1>
              <p className="text-muted-foreground mt-4">
                {status === 'expired' 
                  ? 'Your payment session has expired. Please try again.'
                  : 'There was an issue confirming your payment. If you were charged, please contact us.'}
              </p>
              <div className="flex flex-col gap-3 mt-8">
                <Link to="/cart">
                  <Button className="w-full" data-testid="try-again-btn">
                    Try Again
                  </Button>
                </Link>
                <Link to="/">
                  <Button variant="outline" className="w-full">
                    Back to Home
                  </Button>
                </Link>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
